/**
 * Utility functions to calculate travel times based on distance
 */

/**
 * Calculate walking time based on distance in kilometers
 * Average walking speed is about 5 km/h or 12 minutes per kilometer
 * 
 * @param distance Distance in kilometers
 * @returns Walking time in minutes (rounded to nearest integer)
 */
export const calculateWalkTime = (distance: number): number => {
  if (!distance || isNaN(distance)) return 0;
  
  // Average walking speed: 5 km/h = 12 minutes per km
  const walkingTimeMinutes = distance * 12;
  
  // Round to nearest integer
  return Math.round(walkingTimeMinutes);
};

/**
 * Calculate biking time based on distance in kilometers
 * Average biking speed is about 15 km/h or 4 minutes per kilometer
 * 
 * @param distance Distance in kilometers
 * @returns Biking time in minutes (rounded to nearest integer)
 */
export const calculateBikeTime = (distance: number): number => {
  if (!distance || isNaN(distance)) return 0;
  
  // Average biking speed: 15 km/h = 4 minutes per km
  const bikingTimeMinutes = distance * 4;
  
  // Round to nearest integer
  return Math.round(bikingTimeMinutes);
};

/**
 * Calculate driving time based on distance in kilometers
 * Average driving speed in urban areas is about 30 km/h or 2 minutes per kilometer
 * 
 * @param distance Distance in kilometers
 * @returns Driving time in minutes (rounded to nearest integer)
 */
export const calculateDriveTime = (distance: number): number => {
  if (!distance || isNaN(distance)) return 0;
  
  // Average driving speed: 30 km/h = 2 minutes per km
  const drivingTimeMinutes = distance * 2;
  
  // Round to nearest integer
  return Math.round(drivingTimeMinutes);
};

/**
 * Calculate all travel times based on distance
 * 
 * @param distance Distance in kilometers
 * @returns Object containing walk, bike, and drive times in minutes
 */
export const calculateAllTravelTimes = (distance: number): { 
  walkTime: number; 
  bikeTime: number; 
  driveTime: number;
} => {
  return {
    walkTime: calculateWalkTime(distance),
    bikeTime: calculateBikeTime(distance),
    driveTime: calculateDriveTime(distance)
  };
};